// src/hooks/useWebSocket.js
import { useState, useEffect, useCallback, useRef } from 'react';
import { websocketManager } from '../lib/WebSocketManager';
import { devLog } from '../utils/devLog';

/**
 * useWebSocket Hook
 * React hook that delegates to the singleton WebSocketManager
 * 
 * This hook is now Strict Mode-safe:
 * - Does NOT create its own WebSocket instance
 * - Subscribes/unsubscribes to the shared manager
 * - Manager uses reference counting and debounced close
 * - Connection persists across component remounts
 */
export const useWebSocket = () => {
  const [isConnected, setIsConnected] = useState(websocketManager.isConnected());
  const [isAuthenticated, setIsAuthenticated] = useState(websocketManager.isAuthenticated());
  const [lastMessage, setLastMessage] = useState(null);
  const messageListenerRef = useRef(null);
  const openListenerRef = useRef(null);
  const closeListenerRef = useRef(null);
  const authenticatedListenerRef = useRef(null);

  // Send message through manager
  const send = useCallback((message) => {
    return websocketManager.send(message);
  }, []);

  // Add event listener (returns listener reference for cleanup)
  const addEventListener = useCallback((type, callback) => {
    websocketManager.addListener(type, callback);
    return callback; // Return callback as identifier for removal
  }, []);

  // Remove event listener
  const removeEventListener = useCallback((type, callback) => {
    websocketManager.removeListener(type, callback);
  }, []);

  // Connect (delegates to manager's ensureConnected)
  const connect = useCallback(() => {
    websocketManager.ensureConnected();
  }, []);

  // Disconnect (soft - manager will debounce if this is the last subscriber)
  const disconnect = useCallback(() => {
    // Note: This doesn't immediately close the connection
    // The manager will close after debounce period if no subscribers remain
    devLog('🔌 [Hook] Disconnect requested (manager will handle)');
  }, []);

  // Subscribe to manager on mount, unsubscribe on unmount
  useEffect(() => {
    devLog('🔌 [Hook] Mounting - subscribing to manager');
    
    // Subscribe (increments ref count, ensures connection)
    websocketManager.subscribe();

    // Register event listeners
    messageListenerRef.current = (data) => {
      setLastMessage(data);
    };

    openListenerRef.current = () => {
      setIsConnected(true);
      setIsAuthenticated(false); // Reset authentication state on new connection (will be set to true after auth)
    };

    closeListenerRef.current = () => {
      setIsConnected(false);
      setIsAuthenticated(false); // Reset authentication state on disconnect
    };

    authenticatedListenerRef.current = () => {
      setIsAuthenticated(true);
    };

    websocketManager.addListener('message', messageListenerRef.current);
    websocketManager.addListener('open', openListenerRef.current);
    websocketManager.addListener('close', closeListenerRef.current);
    websocketManager.addListener('authenticated', authenticatedListenerRef.current);

    // Update initial state
    setIsConnected(websocketManager.isConnected());
    setIsAuthenticated(websocketManager.isAuthenticated());

    // Cleanup on unmount
    return () => {
      devLog('🔌 [Hook] Unmounting - unsubscribing from manager');
      
      // Remove listeners
      if (messageListenerRef.current) {
        websocketManager.removeListener('message', messageListenerRef.current);
      }
      if (openListenerRef.current) {
        websocketManager.removeListener('open', openListenerRef.current);
      }
      if (closeListenerRef.current) {
        websocketManager.removeListener('close', closeListenerRef.current);
      }
      if (authenticatedListenerRef.current) {
        websocketManager.removeListener('authenticated', authenticatedListenerRef.current);
      }

      // Unsubscribe (decrements ref count, may trigger debounced close)
      websocketManager.unsubscribe();
    };
  }, []); // Empty deps - only run on mount/unmount

  return {
    isConnected,
    isAuthenticated,
    lastMessage,
    send,
    addEventListener,
    removeEventListener,
    connect,
    disconnect
  };
};

export default useWebSocket;

